import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  InputGroup,
  Container,
  Alert,
  Badge,
} from "reactstrap";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { useGetProducts } from "hooks/useProduct";
import { useGetBranches } from "hooks/useBranches";
import Spinners from "Components/Common/Spinner";
import { useFormik } from "formik";
import Select from "react-select";

import "./producttransfer.css";
import { productTransferValidationSchema } from "utils/rules/productTransfer";
import { useCreateProductTransfer } from "hooks/useProductTransfer";
import { AppRoutes } from "../../constants";
import { useNavigate } from "react-router-dom";
import { ProductTransferType } from "types";
import { BaseButton, ErrorMessage } from "Components";
import { AddNewProduct } from "./components";
import { useSelector } from "react-redux";

const AddProductTransfer: React.FC = () => {
  document.title = "Papermap | Create Product Transfer";

  const navigate = useNavigate();

  const defaultCurrency = useSelector(
    (state: any) => state.Login.default_currency
  );

  const { data: products, isLoading: productsLoading } = useGetProducts();
  const { data: branchData, isLoading: branchesLoading } = useGetBranches();
  const [createNewProduct, setCreateNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    id: "",
    branch_id: "",
  });
  const [transferMode, setTransferMode] = useState<
    "direct" | "custom" | "conversion"
  >("direct");
  const [conversionRatio, setConversionRatio] = useState<number>(24);
  const [availableSourceProductQuantity, setAvailableSourceProductQuantity] =
    useState<number>(0);
  const [availableTargetProductQuantity, setAvailableTargetProductQuantity] =
    useState<number>(0);
  const { mutate: createProductTransfer, isPending } =
    useCreateProductTransfer();
  const [showAddProductModal, setShowAddProductModal] =
    useState<boolean>(false); // State for edit modal

  const toggle = () => {
    setShowAddProductModal(!showAddProductModal);
  };

  const formik = useFormik<ProductTransferType>({
    initialValues: {
      from_branch_id: "",
      from_product_id: "",
      from_quantity: 1,
      to_product_id: "",
      to_branch_id: "",
      price: 0,
      newProductName: "",
      to_quantity: 1,
      status: "draft",
    },
    validationSchema: productTransferValidationSchema(
      createNewProduct,
      availableSourceProductQuantity
    ),
    onSubmit: async (values) => {
      if (createNewProduct) {
        toggle();
      } else {
        // Implement your transfer logic here
        handleProductTransferCreate(formik.values);
      }
    },
  });

  const handleProductTransferCreate = (data) => {
    const payload: any = {
      from_branch_id: data.from_branch_id,
      from_product_id: data.from_product_id,
      from_quantity: data.from_quantity,
      to_branch_id: data.to_branch_id,
      to_product_id: data.to_product_id,
      to_quantity: data.to_quantity,
      price: data.price,
      status: data.status,
    };

    createProductTransfer(payload, {
      onSuccess: (data) => {
        formik.resetForm();
        setCreateNewProduct(false);
        navigate(AppRoutes.PRODUCT_TRANSFERS);
      },
    });
  };

  const filteredfrom_products = products?.filter(
    (product) =>
      String(product.branch_id) === String(formik.values.from_branch_id)
  );

  const filteredto_products = products?.filter(
    (product) =>
      String(product.branch_id) === String(formik.values.to_branch_id)
  );

  useEffect(() => {
    if (newProduct && newProduct.id && newProduct.branch_id) {
      formik.setValues((prevValues) => {
        const updatedValues = {
          ...prevValues,
          to_branch_id: newProduct.branch_id,
          to_product_id: newProduct.id,
        };

        handleProductTransferCreate(updatedValues);

        return updatedValues;
      });
    }
  }, [newProduct]);

  if (productsLoading || branchesLoading) {
    return <Spinners />;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Products" breadcrumbItem="Product Transfer" />

          <Card
            className="border-0 shadow-sm"
            style={{ maxWidth: "1400px", margin: "0 auto" }}
          >
            <CardBody className="p-4">
              <div className="d-flex align-items-center mb-4">
                <h5 className="mb-0">Product Transfer</h5>
                <div className="ms-auto">
                  <Badge color="light" className="text-primary px-3 py-2">
                    <i className="mdi mdi-swap-horizontal me-1"></i>
                    <span style={{ fontSize: "0.85rem" }}>Transfer Mode</span>
                  </Badge>
                </div>
              </div>

              <Alert
                color="primary"
                className="bg-primary bg-opacity-10 border-0 d-flex align-items-center py-2 px-3 mb-4"
              >
                <i
                  className="mdi mdi-information-outline me-2"
                  style={{ fontSize: "1.1rem" }}
                ></i>
                <span style={{ fontSize: "0.85rem" }}>
                  Select source and target branches to transfer products
                </span>
              </Alert>

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  console.log("errors: ", formik.errors);
                  formik.handleSubmit();
                }}
              >
                <Row className="g-4">
                  <Col md={5}>
                    <div className="p-4 h-100">
                      <div className="d-flex align-items-center mb-3">
                        <i
                          className="mdi mdi-arrow-up-circle-outline text-primary me-2"
                          style={{ fontSize: "1.2rem" }}
                        ></i>
                        <h6
                          className="mb-0 text-primary"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Source Details
                        </h6>
                      </div>

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Source Branch
                        </Label>
                        <Select
                          value={branchData?.find(
                            (b) => b.id === formik.values.from_branch_id
                          )}
                          onChange={(option) => {
                            formik.setFieldValue("from_branch_id", option?.id);
                            formik.setFieldValue("from_product_id", "");
                          }}
                          options={branchData}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          aria-invalid="true"
                          styles={{
                            control: (base) => ({
                              ...base,
                              fontSize: "0.85rem",
                              minHeight: "38px",
                            }),
                          }}
                          placeholder="Choose a branch..."
                        />
                        {formik.touched.from_branch_id &&
                          formik.errors.from_branch_id && (
                            <ErrorMessage>
                              {formik.errors.from_branch_id}
                            </ErrorMessage>
                          )}
                      </FormGroup>

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Source Product
                        </Label>
                        <Select
                          value={
                            products?.find(
                              (p) => p.id === formik.values.from_product_id
                            ) || null
                          }
                          onChange={(option) => {
                            formik.setFieldValue("from_product_id", option?.id);
                            setAvailableSourceProductQuantity(option.quantity);
                          }}
                          options={filteredfrom_products}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          styles={{
                            control: (base) => ({
                              ...base,
                              fontSize: "0.85rem",
                              minHeight: "38px",
                            }),
                          }}
                        />

                        {formik.touched.from_product_id &&
                          formik.errors.from_product_id && (
                            <ErrorMessage>
                              {formik.errors.from_product_id}
                            </ErrorMessage>
                          )}
                      </FormGroup>

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Source Quantity
                        </Label>
                        <InputGroup>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            style={{ fontSize: "0.85rem", height: "38px" }}
                            value={formik.values.from_quantity}
                            onChange={(e) => {
                              formik.setFieldValue(
                                "from_quantity",
                                e.target.value
                              );
                              if (transferMode === "direct") {
                                formik.setFieldValue(
                                  "to_quantity",
                                  e.target.value
                                );
                              }
                            }}
                          />
                          <span
                            className="input-group-text"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {products?.find(
                              (p) => p.id === formik.values.from_product_id
                            )?.unit || "units"}
                          </span>
                        </InputGroup>
                        {formik.values.from_product_id && (
                          <small className="text-muted mt-1 d-block">
                            Available: {availableSourceProductQuantity} units
                          </small>
                        )}
                        {formik.touched.from_quantity &&
                          formik.errors.from_quantity && (
                            <ErrorMessage>
                              {formik.errors.from_quantity}
                            </ErrorMessage>
                          )}
                      </FormGroup>
                    </div>
                  </Col>

                  <Col
                    md={2}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <i
                      className="mdi mdi-swap-horizontal text-primary opacity-75"
                      style={{ fontSize: "4rem" }}
                    ></i>
                  </Col>

                  <Col md={5}>
                    <div className="p-4 h-100">
                      <div className="d-flex align-items-center mb-3">
                        <i
                          className="mdi mdi-arrow-down-circle-outline text-primary me-2"
                          style={{ fontSize: "1.2rem" }}
                        ></i>
                        <h6
                          className="mb-0 text-primary"
                          style={{ fontSize: "0.9rem" }}
                        >
                          Target Details
                        </h6>
                      </div>

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Target Branch
                        </Label>
                        <Select
                          value={branchData?.find(
                            (b) => b.id === formik.values.to_branch_id
                          )}
                          onChange={(option) => {
                            formik.setFieldValue("to_branch_id", option?.id);
                            formik.setFieldValue("to_product_id", "");
                          }}
                          options={branchData}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          styles={{
                            control: (base) => ({
                              ...base,
                              fontSize: "0.85rem",
                              minHeight: "38px",
                            }),
                          }}
                          placeholder="Choose a branch..."
                        />
                        {formik.touched.to_branch_id &&
                          formik.errors.to_branch_id && (
                            <ErrorMessage>
                              {formik.errors.to_branch_id}
                            </ErrorMessage>
                          )}
                      </FormGroup>

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Quantity Transfer Mode
                        </Label>
                        <div className="d-flex gap-3 mt-2 flex-wrap">
                          <div
                            className={`transfer-type-option ${
                              transferMode === "direct" ? "active" : ""
                            }`}
                            onClick={() => {
                              setTransferMode("direct");
                              formik.setFieldValue(
                                "to_quantity",
                                formik.values.from_quantity
                              );
                            }}
                          >
                            <Input
                              type="radio"
                              checked={transferMode === "direct"}
                              onChange={() => setTransferMode("direct")}
                            />
                            <i className="mdi mdi-equal text-primary"></i>
                            <span>Same Quantity</span>
                          </div>

                          <div
                            className={`transfer-type-option ${
                              transferMode === "conversion" ? "active" : ""
                            }`}
                            onClick={() => setTransferMode("conversion")}
                          >
                            <Input
                              type="radio"
                              checked={transferMode === "conversion"}
                              onChange={() => setTransferMode("conversion")}
                            />
                            <i className="mdi mdi-cached text-primary"></i>
                            <span>Auto Convert</span>
                          </div>

                          <div
                            className={`transfer-type-option ${
                              transferMode === "custom" ? "active" : ""
                            }`}
                            onClick={() => setTransferMode("custom")}
                          >
                            <Input
                              type="radio"
                              checked={transferMode === "custom"}
                              onChange={() => setTransferMode("custom")}
                            />
                            <i className="mdi mdi-pencil text-primary"></i>
                            <span>Custom Amount</span>
                          </div>
                        </div>
                      </FormGroup>

                      {transferMode === "conversion" && (
                        <FormGroup className="mb-3">
                          <Label
                            className="fw-medium mb-1"
                            style={{ fontSize: "0.85rem" }}
                          >
                            Conversion Ratio
                          </Label>
                          <InputGroup>
                            <Input
                              type="number"
                              min="0.01"
                              step="0.01"
                              style={{ fontSize: "0.85rem", height: "38px" }}
                              value={conversionRatio}
                              onChange={(e) =>
                                setConversionRatio(Number(e.target.value))
                              }
                              placeholder="e.g., 24 for crate to bottles"
                            />
                            <span
                              className="input-group-text"
                              style={{ fontSize: "0.85rem" }}
                            >
                              units
                            </span>
                          </InputGroup>
                        </FormGroup>
                      )}

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Target Product Type
                        </Label>
                        <div className="d-flex gap-3 mb-3 flex-wrap">
                          <div
                            className={`transfer-type-option ${
                              !createNewProduct ? "active" : ""
                            }`}
                            onClick={() => setCreateNewProduct(false)}
                          >
                            <Input
                              type="radio"
                              checked={!createNewProduct}
                              onChange={() => setCreateNewProduct(false)}
                            />
                            <i className="mdi mdi-package-variant text-primary"></i>
                            <span>Existing Product</span>
                          </div>

                          <div
                            className={`transfer-type-option ${
                              createNewProduct ? "active" : ""
                            }`}
                            onClick={() => setCreateNewProduct(true)}
                          >
                            <Input
                              type="radio"
                              checked={createNewProduct}
                              onChange={() => setCreateNewProduct(true)}
                            />
                            <i className="mdi mdi-package-variant-plus text-primary"></i>
                            <span>New Product</span>
                          </div>
                        </div>
                      </FormGroup>

                      {!createNewProduct ? (
                        <FormGroup className="mb-3">
                          <Label
                            className="fw-medium mb-1"
                            style={{ fontSize: "0.85rem" }}
                          >
                            Target Product
                          </Label>
                          <Select
                            value={
                              products?.find(
                                (p) => p.id === formik.values.to_product_id
                              ) || null
                            }
                            onChange={(option) => {
                              formik.setFieldValue("to_product_id", option?.id);
                              setAvailableTargetProductQuantity(
                                option.quantity
                              );
                            }}
                            options={filteredto_products}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            styles={{
                              control: (base) => ({
                                ...base,
                                fontSize: "0.85rem",
                                minHeight: "38px",
                              }),
                            }}
                          />

                          {formik.touched.to_product_id &&
                            formik.errors.to_product_id && (
                              <ErrorMessage>
                                {formik.errors.to_product_id}
                              </ErrorMessage>
                            )}
                        </FormGroup>
                      ) : (
                        <FormGroup className="mb-3">
                          <Label
                            className="fw-medium mb-1"
                            style={{ fontSize: "0.85rem" }}
                          >
                            New Product Name
                          </Label>
                          <Input
                            type="text"
                            style={{ fontSize: "0.85rem", height: "38px" }}
                            {...formik.getFieldProps("newProductName")}
                          />
                          {formik.touched.newProductName &&
                            formik.errors.newProductName && (
                              <ErrorMessage>
                                {formik.errors.newProductName}
                              </ErrorMessage>
                            )}
                        </FormGroup>
                      )}

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          New Price Per Unit
                        </Label>
                        <InputGroup>
                          <span
                            className="input-group-text"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {defaultCurrency}
                          </span>
                          <Input
                            type="number"
                            min="0"
                            style={{ fontSize: "0.85rem", height: "38px" }}
                            value={formik.values.price}
                            onChange={(e) =>
                              formik.setFieldValue("price", e.target.value)
                            }
                            invalid={
                              formik.touched.price && !!formik.errors.price
                            }
                          />
                        </InputGroup>
                        {formik.touched.price && formik.errors.price && (
                          <ErrorMessage>{formik.errors.price}</ErrorMessage>
                        )}
                      </FormGroup>

                      <FormGroup className="mb-3">
                        <Label
                          className="fw-medium mb-1"
                          style={{ fontSize: "0.85rem" }}
                        >
                          Target Quantity
                        </Label>
                        <InputGroup>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            style={{ fontSize: "0.85rem", height: "38px" }}
                            {...formik.getFieldProps("to_quantity")}
                            disabled={transferMode !== "custom"}
                            value={
                              transferMode === "conversion"
                                ? (formik.values.from_quantity || 0) *
                                  conversionRatio
                                : formik.values.to_quantity
                            }
                            onChange={(e) => {
                              if (transferMode === "custom") {
                                formik.setFieldValue(
                                  "to_quantity",
                                  e.target.value
                                );
                              }
                            }}
                          />
                          <span
                            className="input-group-text"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {products?.find(
                              (p) => p.id === formik.values.to_product_id
                            )?.unit || "units"}
                          </span>
                        </InputGroup>
                        {formik.values.to_product_id && (
                          <small className="text-muted mt-1 d-block">
                            Available: {availableTargetProductQuantity} units
                          </small>
                        )}
                        {formik.touched.to_quantity &&
                          formik.errors.to_quantity && (
                            <ErrorMessage>
                              {formik.errors.to_quantity}
                            </ErrorMessage>
                          )}
                      </FormGroup>
                    </div>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <BaseButton
                    type="button"
                    color="light"
                    className="px-4 py-2"
                    style={{ fontSize: "0.85rem" }}
                    onClick={() => {
                      navigate(AppRoutes.PRODUCT_TRANSFERS);
                    }}
                  >
                    Cancel
                  </BaseButton>
                  <BaseButton
                    type="submit"
                    color="primary"
                    className="px-4 py-2 d-flex align-items-center gap-2"
                    style={{ fontSize: "0.85rem" }}
                    loading={isPending}
                  >
                    <span>Transfer Product</span>
                    <i className="mdi mdi-arrow-right-circle"></i>
                  </BaseButton>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
        {/* new product modal */}
        <AddNewProduct
          isOpen={showAddProductModal}
          toggle={toggle}
          newProduct={setNewProduct}
          data={{
            name: formik.values.newProductName,
            branch_id: formik.values.to_branch_id,
            quantity: formik.values.to_quantity,
          }}
        />
      </div>
    </React.Fragment>
  );
};

export default AddProductTransfer;
